import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';
import {
  ALLOWED_CUSTOMER_PERMISSION_SELECTION_FOR_ROLE,
  CUSTOMER_ROLES_EXCLUSIVE,
  CUSTOMER_ROLE_ADMIN_NAMES,
  CUSTOMER_PERMISSION_NAMES,
  CUSTOMER_ROLE_DEFAULT_PERMISSIONS,
  CUSTOMER_PERMISSION_MANDATORY_IN_ROLE,
} from '@/constants/customer.constants';
import { RolesPermissions } from '@/types/permissions';
import { isPermissionEditable } from '@/utils/helpers';

export const DEFAULT_EDIT_PERMISSION_ROLES = {
  roles: Object.keys(CUSTOMER_ROLE_ADMIN_NAMES) as CustomerRoleOnly[],
  permissions: Object.keys(CUSTOMER_PERMISSION_NAMES) as CustomerPermissionOnly[],
};

export const isPermissionMandatory = (permission: CustomerPermissionOnly, roleValues: CustomerRoleOnly[]): boolean => {
  const mandatoryRole = CUSTOMER_PERMISSION_MANDATORY_IN_ROLE[permission];
  return !!mandatoryRole && roleValues.includes(mandatoryRole);
};

export const isPermissionAllowed = (permission: CustomerPermissionOnly, roleValues: CustomerRoleOnly[]): boolean => {
  return (
    ALLOWED_CUSTOMER_PERMISSION_SELECTION_FOR_ROLE[permission]?.some((requiredRole) =>
      roleValues.includes(requiredRole),
    ) ?? false
  );
};

export const resolveRoleConflicts = (newRoles: CustomerRoleOnly[], currentRoles: CustomerRoleOnly[]) => {
  let finalRoles = [...newRoles];
  const addedRole = newRoles.find((role) => !currentRoles.includes(role));

  if (addedRole) {
    const isAddedExclusive = CUSTOMER_ROLES_EXCLUSIVE.includes(addedRole);
    const hasExclusive = newRoles.some((r) => CUSTOMER_ROLES_EXCLUSIVE.includes(r));

    if (isAddedExclusive) {
      // If added role is exclusive, it clears all others
      finalRoles = [addedRole];
    } else if (hasExclusive) {
      // If added role is standard but exclusive roles exist, remove exclusive ones
      finalRoles = newRoles.filter((r) => !CUSTOMER_ROLES_EXCLUSIVE.includes(r));
    }
  }
  return { finalRoles, addedRole };
};

export const getMandatoryPermissions = (roles: CustomerRoleOnly[]): CustomerPermissionOnly[] => {
  return Object.entries(CUSTOMER_PERMISSION_MANDATORY_IN_ROLE)
    .filter(([, role]) => role && roles.includes(role))
    .map(([permission]) => permission as CustomerPermissionOnly);
};

export const getDefaultPermissions = (role: CustomerRoleOnly): CustomerPermissionOnly[] => {
  return CUSTOMER_ROLE_DEFAULT_PERMISSIONS[role] || [];
};

export const getPermissionsToKeep = (
  nextPermissions: CustomerPermissionOnly[],
  finalRoles: CustomerRoleOnly[],
  editPermissionRoles: RolesPermissions,
) => {
  return nextPermissions.filter((permission) => {
    const isEditable = isPermissionEditable(permission, editPermissionRoles.permissions);
    const isMandatory = isPermissionMandatory(permission, finalRoles);
    if (!isEditable || isMandatory) return true;
    return isPermissionAllowed(permission, finalRoles);
  });
};

export const resolvePermissionsForRoles = (
  finalRoles: CustomerRoleOnly[],
  addedRole: CustomerRoleOnly | undefined,
  currentPermissions: CustomerPermissionOnly[],
  editPermissionRoles: RolesPermissions,
): CustomerPermissionOnly[] => {
  const mandatoryPermissions = getMandatoryPermissions(finalRoles);
  const defaultPermissions = addedRole && finalRoles.includes(addedRole) ? getDefaultPermissions(addedRole) : [];

  const nextPermissions = Array.from(new Set([...currentPermissions, ...mandatoryPermissions, ...defaultPermissions]));

  if (addedRole && CUSTOMER_ROLES_EXCLUSIVE.includes(addedRole)) {
    return getPermissionsToKeep([], finalRoles, editPermissionRoles);
  }

  const finalPermissions = getPermissionsToKeep(nextPermissions, finalRoles, editPermissionRoles);
  return finalPermissions;
};

export const hasPermissionsChanged = (
  finalPermissions: CustomerPermissionOnly[],
  currentPermissions: CustomerPermissionOnly[],
): boolean => {
  return (
    finalPermissions.length !== currentPermissions.length ||
    !finalPermissions.every((p) => currentPermissions.includes(p))
  );
};
